
import { useState, useEffect } from "react";
import { Service } from "@/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { BadgeIndianRupee } from "lucide-react";

interface ServiceFormProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentService: Partial<Service>;
  onSave: (service: Partial<Service>) => void;
}

const CATEGORY_OPTIONS = [
  "Consultation",
  "Test",
  "Procedure",
  "Lab Test",
  "Imaging",
  "Surgery",
  "Other"
];

export function ServiceForm({ isOpen, setIsOpen, currentService: initialService, onSave }: ServiceFormProps) {
  const [currentService, setCurrentService] = useState<Partial<Service>>(() => {
    // Initialize with proper category handling
    const defaultCategory = CATEGORY_OPTIONS.includes(initialService.category || "") 
      ? initialService.category 
      : (initialService.category ? "Other" : "Consultation");
    
    return {
      ...initialService,
      category: defaultCategory === "Other" ? initialService.category : defaultCategory
    };
  });
  
  const [categoryType, setCategoryType] = useState(() => {
    return CATEGORY_OPTIONS.includes(initialService.category || "") 
      ? initialService.category 
      : (initialService.category ? "Other" : "Consultation");
  });
  
  const [customCategory, setCustomCategory] = useState(() => {
    return CATEGORY_OPTIONS.includes(initialService.category || "") 
      ? "" 
      : (initialService.category || "");
  });
  const { toast } = useToast();

  // Reset form state when dialog opens with new data
  useEffect(() => {
    if (isOpen) {
      const defaultCategory = CATEGORY_OPTIONS.includes(initialService.category || "") 
        ? initialService.category 
        : (initialService.category ? "Other" : "Consultation");
      
      setCurrentService({
        ...initialService,
        category: defaultCategory === "Other" ? initialService.category : defaultCategory
      });
      
      setCategoryType(defaultCategory);
      
      setCustomCategory(
        CATEGORY_OPTIONS.includes(initialService.category || "") 
          ? "" 
          : (initialService.category || "")
      );
    }
  }, [isOpen, initialService]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentService({ ...currentService, [name]: name === "price" ? Number(value) : value });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryType(e.target.value);
    if (e.target.value !== "Other") {
      setCurrentService({ ...currentService, category: e.target.value });
      setCustomCategory("");
    } else {
      setCurrentService({ ...currentService, category: customCategory });
    }
  };

  const handleCustomCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomCategory(e.target.value);
    setCurrentService({ ...currentService, category: e.target.value });
  };

  const handleSaveService = () => {
    if (!currentService.name || !currentService.category) {
      toast({
        title: "Error",
        description: "Please fill out all required fields",
        variant: "destructive",
      });
      return;
    }
    onSave(currentService);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {currentService.id ? "Edit Service" : "Add New Service"}
          </DialogTitle>
          <DialogDescription>
            Enter the details for the service. Click save when you're done.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Name *
            </Label>
            <Input
              id="name"
              name="name"
              value={currentService.name || ""}
              onChange={handleInputChange}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="category" className="text-right">
              Category *
            </Label>
            <select
              id="category"
              name="category"
              value={categoryType}
              onChange={handleCategoryChange}
              className="col-span-3 border rounded px-2 py-1"
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          {categoryType === "Other" && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="custom-category" className="text-right">
                Custom Category *
              </Label>
              <Input
                id="custom-category"
                name="custom-category"
                value={customCategory}
                onChange={handleCustomCategoryChange}
                className="col-span-3"
                placeholder="Enter custom category"
              />
            </div>
          )}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="price" className="text-right">
              Price (₹)
            </Label>
            <div className="flex items-center col-span-3">
              <span className="mr-2">
                <BadgeIndianRupee className="h-4 w-4" />
              </span>
              <Input
                id="price"
                name="price"
                type="number"
                min="0"
                step="0.01"
                value={currentService.price || 0}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="description" className="text-right">
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              value={currentService.description || ""}
              onChange={handleInputChange}
              className="col-span-3"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSaveService}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
